import { useEffect, useRef, useState } from "react";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import useDebounce from "~/hooks/useDebounce";
import { z } from "zod";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useFileUpload from "~/utils/useFileUpload";
import { getPeriodFromFileYearMonth } from "~/utils/fileUtils";
import useAttachmentFilter from "~/hooks/useAttachmentFilter";
import {
  buildCategoryFoldersFromDocuments,
  getDocumentCategoryCode,
} from "~/utils/categoryFolders";
function useCoreEntityAttachments() {
  const entityData = useSelector((state) => state.generalInformation);

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState<Record<string, any>>({});
  const [restoreMessage, setRstoreMessage] = useState("");
  const [filterData, setFilterData] = useState({});
  const [categoryFolders, setCategoryFolders] = useState<
    { id: string; code: string; name: string; isCategoryFolder?: boolean }[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    code: string;
    name: string;
    isCategoryFolder?: boolean;
  } | null>(null);
  const [chipdata, setChipData] = useState({});
  const removeFnRef = useRef(null);
  const {
    data,
    loading: filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    onPageChange: filterPageChange,
    currentPage,
  } = useAttachmentFilter();

  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const { id: userId, role } = useSelector((state) => state.login);

  const [isFileUploaded, setIsFileUploaded] = useState(true);
  const { id } = useParams();
  const allServices = useSelector(
    (state) => state.globalEntities.entityservices,
  );
  const getService = () => {
    const service = allServices.find((service) => {
      return service?.id === {}?.service;
    });
    return service;
  };
  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];
  const uploadFileHandler = async (files) => {
    setIsFileUploaded(false);

    try {
      const formData = new FormData();
      const fileOutput = [];
      const attachments = [];
      let tags, metadata;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        fileOutput.push(fileData);
        formData.append("filesData", JSON.stringify(fileOutput));

        tags = {
          entity_id: uuidToUlid(entityData?.generalInformation?.data?.id),
          country: entityData?.generalInformation?.data?.country,
          core_entity_info: "Core Entity Information",
          temporary_file: "false",
          category: file?.category,
          subCategory: file?.subcategory,
          year: new Date().getFullYear().toString(),
          ...(getPeriodFromFileYearMonth(file?.fileYearMonth) && {
            period: getPeriodFromFileYearMonth(file.fileYearMonth),
          }),
        };
        const version = `${await getFileVersion(tags)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          file_object_type: "ENTITIES_PLUS",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };
        const uploadData = await uploadFile(file, file.fileName, {
          entityId: entityData?.generalInformation?.data?.id,
          serviceLineCode: "ENTITIES_PLUS",
          fileName: file.name,
        });
        metadata = { ...metadata, url: uploadData.url };
        attachments.push({
          url: uploadData.url,
          fileName: uploadData.id,
          name: file.fileName,
        });
        await updateTag(uploadData.id, tags, metadata);
        await apiService(API_ROUTES.ADD_DOCUMENT_AUDIT, "POST", {
          body: { attachments },
          replace: { id, type: "ENTITIES_PLUS" },
          schema: z.object({
            id: z.string().uuid(),
            type: z.enum(["ENTITIES_PLUS"]),
          }),
        });
      }
    } catch (e) {
      setIsFileUploaded(false);
      console.error(e);
    } finally {
      // Introduced a delay to allow time for the virus scan to complete after file upload,
      // ensuring the document list fetch reflects the latest and clean files.
      setTimeout(async () => {
        await fetchOfficerAttachments(true); // GXM-1769: skipCache after upload
        setSuccessMessage("File uploaded successfully");
        setIsFileUploaded(true);
        setShowUploadModal(false);
      }, 2000);
    }
  };
  const fetchOfficerAttachments = async (skipCache = false) => {
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id, type: "ENTITIES_PLUS" },
      skipCache,
    });
    if (data?.length) {
      setIsDataAvailable(true);
      setAttachments(data);
      setCategoryFolders(buildCategoryFoldersFromDocuments(data));
    } else {
      setIsDataAvailable(false);
      setAttachments([]);
      setCategoryFolders([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOfficerAttachments();
  }, []);

  const handleChangePage = (e) => {
    setPage(e.page);
    setRows(e.rows);
    setFirst(e.first);
  };

  const handleOpenDialog = (detail) => {
    setSelectedUser(detail);
    setOpenDialog(true);
  };

  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const selectedItems = Object.values(filterData || {}).filter(
    (value) =>
      !["category", "service_line", "entities", "year"].includes(value?.id),
  );
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");

    const { status } = await apiService(
      API_ROUTES.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: { relatedId: id, relatedType: "ENTITIES_PLUS" },
        schema: z.object({
          relatedId: z.string().uuid(),
          relatedType: z.enum(["ENTITIES_PLUS"]),
        }),
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchOfficerAttachments(true); // GXM-1769: skipCache after delete
        }
        setAttachmentConfirmModal(false);
        setTemporaryMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been deleted.`,
        );
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };
  const handleRestoreAttachment = async () => {
    setLoading(true);
    setTemporaryMessage("");
    setRstoreMessage("");
    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
    });
    if (status) {
      setTimeout(async () => {
        if (selectedItems?.length) {
          await onFilterApply();
        } else {
          await fetchOfficerAttachments(true); // GXM-1769: skipCache after restore
        }
        setRstoreMessage(
          `${selectedFile?.displayName || selectedFile?.name} has been restored.`,
        );
      }, 2000);
    }
  };

  const handleRemoveFilter = (id) => {
    if (removeFnRef.current) {
      const { value: updatedValue, grouped } = removeFnRef.current(id);
      setFilterData(updatedValue);
      handleFilterChange(grouped);
    }
  };
  const handleFilterChange = (data) => {
    const categories = Object.entries(data?.category || {}).map(
      ([key, value]) => ({
        category: key,
        subCategories: value?.map((sub) =>
          sub.includes("/") ? sub.split("/").pop() : sub,
        ),
      }),
    );
    setChipData(categories);
    onFilterChange({
      categories,
      typeId: id,
      type: "ENTITIES_PLUS",
    });
  };

  useEffect(() => {
    if (selectedItems?.length) {
      onFilterApply();
    } else {
      fetchOfficerAttachments();
    }
  }, [filterData]);

  // Publish attachment files in Core Entity Info
  const promoteAttachmentsToEntityInfo = async (filePayload) => {
    try {
      setLoading(true);
      return await apiService(API_ROUTES.PROMOTE_ATTACHMENT_TO_ENTITY, "POST", {
        body: filePayload,
        suppressToast: true,
        schema: z.object({
          containerName: z.string(),
          blobName: z.string(),
          category: z.string(),
          subCategory: z.string(),
        }),
      });
    } catch (error) {
      throw new Error("Error promoting attachments to entity info:", error);
    } finally {
      setLoading(false);
    }
  };

  // Publish attachment files in Core Entity Info
  const unpublishAttachmentsFromEntityInfo = async (filePayload) => {
    try {
      setLoading(true);
      return await apiService(API_ROUTES.UNPUBLISH_DOCUMENT, "POST", {
        body: filePayload,
        suppressToast: true,
        schema: z.object({
          containerName: z.string(),
          blobName: z.string(),
        }),
      });
    } catch (error) {
      throw new Error(
        "Error unpublishing attachments from entity info:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };
  const handleCategoryFolderClick = (folder: {
    id: string;
    code: string;
    name: string;
  }) => {
    setSelectedCategory(folder);
  };

  const displayAttachments = selectedCategory
    ? attachments.filter(
        (f) => getDocumentCategoryCode(f) === selectedCategory.code,
      )
    : attachments;

  return {
    openDialog,
    setOpenDialog,
    selectedUser,
    setSelectedUser,
    attachments,
    loading,
    setLoading,
    page,
    rows,
    totalRecords,
    onPageChange: handleChangePage,
    handleOpenDialog,
    isDataAvailable,
    entitydata: entityData?.generalInformation?.data,
    showUploadModal,
    fileSupported,
    setShowUploadModal,
    uploadFileHandler,
    getService,
    requestdata: [],
    isFileUploaded,
    successMessage,
    setSuccessMessage,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
    first,
    handleDeleteDocument,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    handleRestoreAttachment,
    temporaryMessage,
    isAttachmentLoader,
    setTemporaryMessage,
    restoreMessage,
    setRstoreMessage,
    role,
    selectedItems,
    filterData,
    setFilterData,
    chipdata,
    setChipData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    data,
    filterLoading,
    error,
    onFilterChange,
    onFilterApply,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
    fetchOfficerAttachments,
    promoteAttachmentsToEntityInfo,
    unpublishAttachmentsFromEntityInfo,
    categoryFolders,
    selectedCategory,
    setSelectedCategory,
    handleCategoryFolderClick,
    displayAttachments,
  };
}

export default useCoreEntityAttachments;
